import { useId, useState } from "react";
import { CMSComponents, type CMSComponentProps } from "../engine/CMSComponent.tsx";
import KitButton, { ChevronRight, useLink } from "./KitButton.tsx";
import { SectionShell } from "./Section.tsx";

const str = (value: unknown) => (typeof value === "string" ? value : undefined);

export function ButtonGroup({ node }: CMSComponentProps) {
  const alignment = String(node.alignment ?? "start").toLowerCase();
  return (
    <div className={`cms-button-group align-${alignment}`}>
      <CMSComponents nodes={node.button} />
    </div>
  );
}

export function Button({ node }: CMSComponentProps) {
  const variant = /secondary/i.test(str(node.type) ?? "") ? "secondary" : "primary";
  const link = useLink(str(node.url));

  return (
    <KitButton {...link} href={link.href ?? "#"} variant={variant} aria-label={str(node.ariaLabel)}>
      {str(node.text)}
      {node.icon === "chevron" && <ChevronRight />}
    </KitButton>
  );
}

export function InlinePlayButton({ node }: CMSComponentProps) {
  const youTubeId = str(node.youTubeId);
  if (!youTubeId) return null;
  return (
    <a
      className="cms-play-button"
      href={`https://www.youtube.com/watch?v=${youTubeId}`}
      aria-label={str(node.ariaLabel)}
      target="_blank"
      rel="noreferrer"
    >
      ▶ {str(node.text)}
    </a>
  );
}

/** Tabs that switch between referenced sections; client state after hydration. */
export function PageSegmentControl({ node }: CMSComponentProps) {
  const [active, setActive] = useState(0);
  const baseId = useId();
  const tabs = [1, 2, 3]
    .map((n) => ({ label: str(node[`tab${n}`]), items: node[`collection${n}`] }))
    .filter((tab) => tab.label);

  return (
    <SectionShell node={node} className="cms-segments">
      <div
        role="tablist"
        className={`cms-tabs align-${String(node.tabAlignment ?? "center").toLowerCase()}`}
      >
        {tabs.map((tab, index) => (
          <button
            key={tab.label}
            type="button"
            role="tab"
            id={`${baseId}-tab-${index}`}
            aria-controls={`${baseId}-panel-${index}`}
            aria-selected={active === index}
            onClick={() => setActive(index)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {tabs.map((tab, index) => (
        <div
          key={tab.label}
          role="tabpanel"
          id={`${baseId}-panel-${index}`}
          aria-labelledby={`${baseId}-tab-${index}`}
          hidden={active !== index}
        >
          <CMSComponents nodes={tab.items} />
        </div>
      ))}
    </SectionShell>
  );
}
